import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, Briefcase, Calendar, Clock, Coffee, FileText, HandHelping, MapPin, RefreshCw, Users } from 'lucide-react';
import { shiftApi, schedulePublicationStatusApi, shiftMarketplaceApi } from '../services/api';
import { Shift, ShiftColleagueDto, ShiftMarketplaceRequest } from '../types';
import { getShiftTotalHours } from '../utils/shiftHours';
import {
  indexMarketplaceByShiftId,
  marketplaceAccepterBadge,
  marketplaceOffererBadge,
} from '../utils/shiftMarketplacePending';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, parseISO } from 'date-fns';
import { WeekNavigator } from '../components/WeekNavigator';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { parseLocalDate } from '../utils/payrollPeriodLocal';
import { formatCompanyDate } from '../utils/companyDateFormat';

const parseLocalDateTime = (dateTimeStr: string): Date => {
  if (!dateTimeStr) return new Date();
  try {
    if (!dateTimeStr.includes('Z') && !dateTimeStr.includes('+') && !dateTimeStr.includes('-', 10)) {
      const [datePart, timePart] = dateTimeStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second || 0);
    }
    return parseISO(dateTimeStr);
  } catch {
    return new Date(dateTimeStr);
  }
};

const MySchedules: React.FC = () => {
  const { employee } = useAuth();
  const { hasPermission } = usePermissions();
  const canMarketplaceProposals = hasPermission('marketplace_proposals');
  const weekStartsOn = useWeekStartDay();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [publicationStatuses, setPublicationStatuses] = useState<Map<string, { status: string }>>(new Map());
  const [colleaguesByShiftId, setColleaguesByShiftId] = useState<Record<number, ShiftColleagueDto[]>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusyShiftId, setActionBusyShiftId] = useState<number | null>(null);
  const [marketplaceRequests, setMarketplaceRequests] = useState<ShiftMarketplaceRequest[]>([]);
  const [swapForShift, setSwapForShift] = useState<Shift | null>(null);
  const [coverForShift, setCoverForShift] = useState<Shift | null>(null);
  const [proposalNotes, setProposalNotes] = useState('');
  const [swapTargetId, setSwapTargetId] = useState<number | ''>('');
  const [peerShifts, setPeerShifts] = useState<Shift[]>([]);
  const [loadingPeers, setLoadingPeers] = useState(false);

  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const fetchShifts = async () => {
    if (!employee?.id) return;
    try {
      setLoading(true);
      const startDateStr = format(weekStart, 'yyyy-MM-dd');
      const endDateStr = format(weekEnd, 'yyyy-MM-dd');
      const shiftsData = await shiftApi.getAll({
        startDate: startDateStr,
        endDate: endDateStr,
        employeeId: employee.id,
        publishedOnly: true,
        bypassCache: true,
      });
      setShifts(shiftsData);

      const statusMap = new Map<string, { status: string }>();
      const uniqueKeys = new Set<string>();
      shiftsData.forEach((shift: Shift) => {
        if (!shift.locationId || !shift.scheduleId) return;
        const shiftWeekStart = format(
          startOfWeek(parseLocalDateTime(shift.startTime), { weekStartsOn }),
          'yyyy-MM-dd'
        );
        uniqueKeys.add(`${shift.locationId}:${shift.scheduleId}:${shiftWeekStart}`);
      });

      await Promise.all(
        Array.from(uniqueKeys).map(async (key) => {
          const [locationIdStr, scheduleIdStr, shiftWeekStart] = key.split(':');
          try {
            const status = await schedulePublicationStatusApi.get(
              Number(locationIdStr),
              Number(scheduleIdStr),
              shiftWeekStart
            );
            statusMap.set(key, status);
          } catch {
            // Do not hide shifts when the status call fails.
            statusMap.set(key, { status: 'Published' });
          }
        })
      );
      setPublicationStatuses(statusMap);

      try {
        const colleaguesRes = await shiftApi.getColleagues({
          employeeId: employee.id,
          startDate: startDateStr,
          endDate: endDateStr,
        });
        const parsed: Record<number, ShiftColleagueDto[]> = {};
        for (const [key, list] of Object.entries(colleaguesRes.colleaguesByShiftId ?? {})) {
          parsed[Number(key)] = list;
        }
        setColleaguesByShiftId(parsed);
      } catch {
        setColleaguesByShiftId({});
      }

      try {
        const mine = await shiftMarketplaceApi.getRequests({ mine: true });
        setMarketplaceRequests(mine);
      } catch {
        setMarketplaceRequests([]);
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
      setColleaguesByShiftId({});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, [weekStart, weekEnd, employee?.id]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchShifts();
  };

  const getPublicationStatus = (shift: Shift): string => {
    if (!shift.locationId || !shift.scheduleId) return 'Published';
    const shiftWeekStart = format(
      startOfWeek(parseLocalDateTime(shift.startTime), { weekStartsOn }),
      'yyyy-MM-dd'
    );
    return (
      publicationStatuses.get(`${shift.locationId}:${shift.scheduleId}:${shiftWeekStart}`)?.status ||
      'Published'
    );
  };

  const getShiftsForDay = (date: Date): Shift[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts
      .filter(shift => {
        const shiftDate = format(parseLocalDateTime(shift.startTime), 'yyyy-MM-dd');
        return shiftDate === dateStr;
      })
      .filter(shift => getPublicationStatus(shift) !== 'Unpublished')
      .sort((a, b) => parseLocalDateTime(a.startTime).getTime() - parseLocalDateTime(b.startTime).getTime());
  };

  const formatTime = (dateTimeStr: string): string => format(parseLocalDateTime(dateTimeStr), 'HH:mm');

  const formatBreakTime = (unpaidHours: number): string => {
    const minutes = Math.round((unpaidHours || 0) * 60);
    if (minutes === 0) return 'No break';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const marketplaceByShiftId = useMemo(
    () => indexMarketplaceByShiftId(marketplaceRequests),
    [marketplaceRequests]
  );

  const incomingPendingCovers = useMemo(() => {
    if (!employee?.id) return [];
    return marketplaceRequests.filter(
      (r) =>
        r.status === 'PendingApproval' &&
        r.acceptedByEmployeeId === employee.id &&
        r.offeredByEmployeeId !== employee.id &&
        r.offeredShift?.startTime
    );
  }, [marketplaceRequests, employee?.id]);

  const getIncomingForDay = (date: Date): ShiftMarketplaceRequest[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return incomingPendingCovers.filter((r) => {
      const start = r.offeredShift?.startTime;
      if (!start) return false;
      return format(parseLocalDateTime(start), 'yyyy-MM-dd') === dateStr;
    });
  };

  const ownedMarketplaceBadge = (shift: Shift, request: ShiftMarketplaceRequest) => {
    if (request.offeredByEmployeeId === employee?.id) return marketplaceOffererBadge(request);
    return marketplaceAccepterBadge(request);
  };

  const publishedOrChangedShifts = shifts.filter(shift => getPublicationStatus(shift) !== 'Unpublished');
  const totalScheduledHours = publishedOrChangedShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
  const uniqueLocations = new Set(
    publishedOrChangedShifts.map(s => (s as any).location?.name).filter(Boolean)
  );
  const locationsList = Array.from(uniqueLocations);

  const isFutureShift = (shift: Shift) => parseLocalDateTime(shift.startTime).getTime() > Date.now() + 2 * 60 * 60 * 1000;

  const openCoverModal = (shift: Shift) => {
    setCoverForShift(shift);
    setProposalNotes('');
    setActionError(null);
  };

  const submitCover = async () => {
    if (!coverForShift) return;
    try {
      setActionBusyShiftId(coverForShift.id);
      setActionError(null);
      const notes = proposalNotes.trim();
      await shiftMarketplaceApi.create({
        requestType: 'Giveaway',
        offeredShiftId: coverForShift.id,
        notes: notes || undefined,
      });
      setCoverForShift(null);
      const mine = await shiftMarketplaceApi.getRequests({ mine: true });
      setMarketplaceRequests(mine);
    } catch (e: any) {
      setActionError(e?.response?.data?.message || 'Failed to offer shift');
    } finally {
      setActionBusyShiftId(null);
    }
  };

  const openSwapModal = async (shift: Shift) => {
    setSwapForShift(shift);
    setSwapTargetId('');
    setProposalNotes('');
    setPeerShifts([]);
    setLoadingPeers(true);
    setActionError(null);
    try {
      const start = format(weekStart, 'yyyy-MM-dd');
      const end = format(addWeeks(weekEnd, 1), 'yyyy-MM-dd');
      const all = await shiftApi.getAll({
        startDate: start,
        endDate: end,
        locationId: shift.locationId,
      });
      setPeerShifts(
        all.filter(
          (s) =>
            s.employeeId !== employee?.id &&
            s.locationId === shift.locationId &&
            parseLocalDateTime(s.startTime).getTime() > Date.now()
        )
      );
    } catch {
      setPeerShifts([]);
      setActionError('Could not load peer shifts for swap.');
    } finally {
      setLoadingPeers(false);
    }
  };

  const submitSwap = async () => {
    if (!swapForShift || !swapTargetId) return;
    try {
      setActionBusyShiftId(swapForShift.id);
      setActionError(null);
      const notes = proposalNotes.trim();
      await shiftMarketplaceApi.create({
        requestType: 'Swap',
        offeredShiftId: swapForShift.id,
        requestedShiftId: Number(swapTargetId),
        notes: notes || undefined,
      });
      setSwapForShift(null);
      const mine = await shiftMarketplaceApi.getRequests({ mine: true });
      setMarketplaceRequests(mine);
    } catch (e: any) {
      setActionError(e?.response?.data?.message || 'Failed to propose swap');
    } finally {
      setActionBusyShiftId(null);
    }
  };

  const renderMarketplaceBadge = (label: string) => (
    <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-xs font-medium text-amber-900">
      {label}
    </div>
  );

  const renderShiftActions = (shift: Shift) => {
    const pending = marketplaceByShiftId.get(shift.id);
    if (pending) {
      return renderMarketplaceBadge(ownedMarketplaceBadge(shift, pending));
    }
    if (!canMarketplaceProposals || !isFutureShift(shift)) return null;
    return (
      <div className="mt-2 flex items-center gap-1.5">
        <button
          type="button"
          disabled={actionBusyShiftId === shift.id}
          onClick={() => openCoverModal(shift)}
          title="Cover"
          aria-label="Cover"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <HandHelping className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button
          type="button"
          disabled={actionBusyShiftId === shift.id}
          onClick={() => openSwapModal(shift)}
          title="Swap"
          aria-label="Swap"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-neutral-300 text-neutral-700 hover:bg-white disabled:opacity-50"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    );
  };

  const renderColleagues = (shift: Shift) => {
    const colleagues = colleaguesByShiftId[shift.id];
    if (!colleagues?.length) return null;
    return (
      <div className="mt-2 border-t border-neutral-200 pt-2">
        <div className="flex items-center gap-1.5 font-medium text-neutral-700">
          <Users className="h-3.5 w-3.5 shrink-0 text-neutral-500" aria-hidden />
          Working with
        </div>
        <ul className="mt-1 space-y-1 pl-5">
          {colleagues.map((c) => (
            <li key={`${shift.id}-${c.employeeId}`}>
              {c.firstName} {c.lastName}
              {c.positionTitle ? <span className="text-neutral-500"> · {c.positionTitle}</span> : null}
              <span className="text-neutral-500">
                {' '}
                · {format(parseLocalDateTime(c.startTime), 'HH:mm')}–
                {format(parseLocalDateTime(c.endTime), 'HH:mm')}
              </span>
              {c.scheduleName ? <span className="text-neutral-400"> ({c.scheduleName})</span> : null}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const detailRow = (
    Icon: React.ComponentType<{ className?: string }>,
    text: string,
    opts?: { title?: string; className?: string; italic?: boolean }
  ) => (
    <div
      className={`flex items-start gap-1.5 min-w-0 ${opts?.className ?? ''}`}
      title={opts?.title ?? text}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5 text-neutral-400" aria-hidden />
      <span className={`min-w-0 break-words ${opts?.italic ? 'italic' : ''}`}>{text}</span>
    </div>
  );

  const renderMobileShiftCard = (shift: Shift) => (
    <div
      key={shift.id}
      className="flex items-start gap-4 p-3 rounded-lg border-l-4 border-blue-500 bg-neutral-50"
    >
      <div
        className="w-4 h-4 rounded-full flex-shrink-0 mt-1"
        style={{ backgroundColor: shift.color || '#3b82f6' }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-base font-semibold text-neutral-900">
          <Clock className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
          {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
        </div>
        <div className="mt-2 text-sm text-neutral-600 space-y-1.5">
          {detailRow(MapPin, (shift as any).location?.name || 'N/A')}
          {detailRow(Calendar, shift.schedule?.name || 'N/A')}
          {shift.position && detailRow(Briefcase, shift.position.title)}
          {detailRow(Clock, `${getShiftTotalHours(shift).toFixed(1)}h`)}
          {detailRow(Coffee, formatBreakTime(shift.unpaidHours || 0))}
          {shift.notes && detailRow(FileText, shift.notes, { italic: true })}
          {renderColleagues(shift)}
          {renderShiftActions(shift)}
        </div>
      </div>
    </div>
  );

  const renderIncomingPendingCard = (request: ShiftMarketplaceRequest) => {
    const start = request.offeredShift?.startTime;
    const end = request.offeredShift?.endTime;
    return (
      <div
        key={`incoming-${request.id}`}
        className="rounded-md border border-dashed border-amber-300 bg-amber-50/70 p-3"
      >
        {start && end && (
          <div className="flex items-center gap-1.5 text-base font-semibold text-neutral-900 leading-tight">
            <Clock className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
            {formatTime(start)}–{formatTime(end)}
          </div>
        )}
        <div className="mt-2 space-y-1.5 text-sm text-neutral-600">
          {detailRow(MapPin, request.locationName || request.offeredShift?.scheduleName || 'Location')}
          {request.offeredShift?.scheduleName && detailRow(Calendar, request.offeredShift.scheduleName)}
          {request.offeredShift?.positionTitle && detailRow(Briefcase, request.offeredShift.positionTitle)}
        </div>
        {renderMarketplaceBadge(marketplaceAccepterBadge(request))}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h1 className="text-2xl font-bold text-neutral-800">My Schedules</h1>
        {canMarketplaceProposals && (
          <Link
            to="/shift-marketplace"
            className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            Marketplace
          </Link>
        )}
      </div>

      {actionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{actionError}</div>
      )}

      {/* Week navigator + summary */}
      <div className="flex flex-col gap-3 md:flex-row md:items-stretch md:gap-4">
        <div className="flex flex-1 items-center justify-center gap-4 bg-white rounded-lg shadow-sm border border-neutral-200 px-4 py-3">
          <WeekNavigator
            variant="neutral"
            weekStart={weekStart}
            weekEnd={weekEnd}
            weekStartsOn={weekStartsOn}
            currentWeek={currentWeek}
            onPreviousWeek={() => setCurrentWeek(subWeeks(currentWeek, 1))}
            onNextWeek={() => setCurrentWeek(addWeeks(currentWeek, 1))}
            onCurrentWeek={() => setCurrentWeek(new Date())}
            onWeekSelect={(ymd) => setCurrentWeek(parseLocalDate(ymd))}
            currentWeekLabel="This Week"
            endContent={
              <button
                onClick={onRefresh}
                disabled={refreshing}
                className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 transition-colors disabled:opacity-50"
                aria-label="Refresh"
              >
                <RefreshCw className={`h-5 w-5 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            }
          />
        </div>

        {!loading && (
          <div className="grid grid-cols-3 gap-2 md:flex md:shrink-0 md:gap-2">
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 px-3 py-2 text-center md:min-w-[7.5rem]">
              <div className="text-lg font-bold text-blue-600 leading-tight">{publishedOrChangedShifts.length}</div>
              <div className="text-xs text-neutral-500 leading-tight">Shifts</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 px-3 py-2 text-center md:min-w-[7.5rem]">
              <div className="text-lg font-bold text-blue-600 leading-tight">{totalScheduledHours.toFixed(1)}h</div>
              <div className="text-xs text-neutral-500 leading-tight">Hours</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 px-3 py-2 text-center md:min-w-[7.5rem]">
              <div className="text-lg font-bold text-blue-600 leading-tight">{locationsList.length}</div>
              <div className="text-xs text-neutral-500 leading-tight">Locations</div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading schedules...</div>
        </div>
      ) : (
        <>
          {/* Daily Schedule — Mobile (stacked cards) */}
          <div className="md:hidden space-y-4">
            <h2 className="text-lg font-semibold text-neutral-800">Daily Schedule</h2>
            {weekDays.map(day => {
              const dayShifts = getShiftsForDay(day);
              const incoming = getIncomingForDay(day);
              const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
              const hasChanged = dayShifts.some(s => getPublicationStatus(s) === 'Changed');

              return (
                <div
                  key={day.toISOString()}
                  className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden"
                >
                  <div className={`px-4 py-3 border-b border-neutral-200 ${isToday ? 'bg-blue-50' : 'bg-neutral-50'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`font-semibold ${isToday ? 'text-blue-700' : 'text-neutral-700'}`}>
                        {format(day, 'EEEE')} - {formatCompanyDate(day)}
                      </span>
                      {hasChanged && (
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800 shrink-0">
                          Schedule Changed
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    {dayShifts.length === 0 && incoming.length === 0 ? (
                      <div className="text-center text-neutral-500 py-4">No Shifts Scheduled</div>
                    ) : (
                      <div className="space-y-3">
                        {dayShifts.map(renderMobileShiftCard)}
                        {incoming.map(renderIncomingPendingCard)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Weekly Schedule — Desktop (week grid only) */}
          <div className="hidden md:block space-y-4">
            <h2 className="text-lg font-semibold text-neutral-800">Weekly Schedule</h2>
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
              <div className="grid grid-cols-7 divide-x divide-neutral-200 border-b border-neutral-200">
                {weekDays.map(day => {
                  const dayShifts = getShiftsForDay(day);
                  const incoming = getIncomingForDay(day);
                  const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  const dayHours = dayShifts.reduce((sum, s) => sum + getShiftTotalHours(s), 0);
                  const dayCount = dayShifts.length + incoming.length;
                  return (
                    <div
                      key={`hdr-${day.toISOString()}`}
                      className={`px-2 py-3 text-center ${isToday ? 'bg-blue-50' : 'bg-neutral-50'}`}
                    >
                      <div className={`text-sm font-medium uppercase tracking-wide ${isToday ? 'text-blue-600' : 'text-neutral-500'}`}>
                        {format(day, 'EEE')}
                      </div>
                      <div className={`text-xl font-semibold ${isToday ? 'text-blue-700' : 'text-neutral-800'}`}>
                        {format(day, 'd')}
                      </div>
                      <div className="text-sm text-neutral-500 mt-0.5">
                        {dayCount === 0
                          ? 'Off'
                          : `${dayCount} shift${dayCount === 1 ? '' : 's'} · ${dayHours.toFixed(1)}h`}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-7 divide-x divide-neutral-200 min-h-[160px]">
                {weekDays.map(day => {
                  const dayShifts = getShiftsForDay(day);
                  const incoming = getIncomingForDay(day);
                  const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  const hasChanged = dayShifts.some(s => getPublicationStatus(s) === 'Changed');
                  return (
                    <div
                      key={`col-${day.toISOString()}`}
                      className={`p-2 space-y-2 ${isToday ? 'bg-blue-50/40' : ''}`}
                    >
                      {hasChanged && (
                        <div className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-center leading-tight">
                          Changed
                        </div>
                      )}
                      {dayShifts.length === 0 && incoming.length === 0 ? (
                        <div className="text-center text-xs text-neutral-400 py-6">—</div>
                      ) : (
                        <>
                        {dayShifts.map(shift => {
                          const colleagues = colleaguesByShiftId[shift.id] ?? [];
                          return (
                            <div
                              key={shift.id}
                              className="rounded-md border border-neutral-200 bg-white p-3 shadow-sm"
                              style={{ borderLeftWidth: 3, borderLeftColor: shift.color || '#3b82f6' }}
                            >
                              <div className="flex items-center gap-1.5 text-base font-semibold text-neutral-900 leading-tight">
                                <Clock className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
                                {formatTime(shift.startTime)}–{formatTime(shift.endTime)}
                              </div>
                              <div className="mt-2 space-y-1.5 text-sm text-neutral-600">
                                {detailRow(MapPin, (shift as any).location?.name || 'N/A')}
                                {shift.schedule?.name && detailRow(Calendar, shift.schedule.name)}
                                {shift.position && detailRow(Briefcase, shift.position.title)}
                                {detailRow(Clock, `${getShiftTotalHours(shift).toFixed(1)}h`)}
                                {detailRow(Coffee, formatBreakTime(shift.unpaidHours || 0))}
                                {shift.notes && detailRow(FileText, shift.notes, { italic: true })}
                                {colleagues.length > 0 && (
                                  <div className="pt-1.5 border-t border-neutral-100 mt-1.5">
                                    <div className="flex items-center gap-1.5 font-medium text-neutral-700 mb-1">
                                      <Users className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
                                      With
                                    </div>
                                    {colleagues.map((c) => (
                                      <div
                                        key={`${shift.id}-${c.employeeId}`}
                                        className="pl-5 truncate text-neutral-500"
                                        title={`${c.firstName} ${c.lastName}`}
                                      >
                                        {c.firstName} {c.lastName}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {renderShiftActions(shift)}
                            </div>
                          );
                        })}
                        {incoming.map(renderIncomingPendingCard)}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {swapForShift && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">Propose swap</h3>
            <p className="text-sm text-neutral-600">
              Your shift: {formatTime(swapForShift.startTime)} – {formatTime(swapForShift.endTime)} on{' '}
              {format(parseLocalDateTime(swapForShift.startTime), 'EEE d MMM')}
            </p>
            {loadingPeers ? (
              <div className="text-sm text-neutral-500">Loading peer shifts…</div>
            ) : peerShifts.length === 0 ? (
              <div className="text-sm text-neutral-500">No peer shifts found at this location in range.</div>
            ) : (
              <label className="block text-sm">
                <span className="text-neutral-700 font-medium">Swap with</span>
                <select
                  className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2"
                  value={swapTargetId}
                  onChange={(e) => setSwapTargetId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Select a shift…</option>
                  {peerShifts.map((s) => (
                    <option key={s.id} value={s.id}>
                      {(s as any).employee
                        ? `${(s as any).employee.firstName} ${(s as any).employee.lastName}`
                        : `Employee #${s.employeeId}`}
                      {' · '}
                      {format(parseLocalDateTime(s.startTime), 'EEE d MMM HH:mm')}–
                      {format(parseLocalDateTime(s.endTime), 'HH:mm')}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="block text-sm">
              <span className="text-neutral-700 font-medium">Comment (optional)</span>
              <textarea
                className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2"
                rows={3}
                maxLength={500}
                value={proposalNotes}
                onChange={(e) => setProposalNotes(e.target.value)}
                placeholder="Why are you requesting this swap?"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded-md border border-neutral-300 text-sm"
                onClick={() => setSwapForShift(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!swapTargetId || actionBusyShiftId === swapForShift.id}
                className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-50"
                onClick={submitSwap}
              >
                Propose
              </button>
            </div>
          </div>
        </div>
      )}

      {coverForShift && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">Request shift cover</h3>
            <p className="text-sm text-neutral-600">
              Your shift: {formatTime(coverForShift.startTime)} – {formatTime(coverForShift.endTime)} on{' '}
              {format(parseLocalDateTime(coverForShift.startTime), 'EEE d MMM')}
            </p>
            <label className="block text-sm">
              <span className="text-neutral-700 font-medium">Comment (optional)</span>
              <textarea
                className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2"
                rows={3}
                maxLength={500}
                value={proposalNotes}
                onChange={(e) => setProposalNotes(e.target.value)}
                placeholder="Why are you offering this shift for cover?"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded-md border border-neutral-300 text-sm"
                onClick={() => setCoverForShift(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionBusyShiftId === coverForShift.id}
                className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-50"
                onClick={() => void submitCover()}
              >
                Request cover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySchedules;
